import { Component, Inject } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { extractApiErrorMessage } from '../../shared/utils/extract-api-error-message.util';
import { OrderSupplierCancellationPreview, OrderSupplierCancellationService } from './order-supplier-cancellation.service';

export interface SupplierCancellationDialogData {
  preview: OrderSupplierCancellationPreview;
  provider: string;
  product: string;
  article: string;
}

@Component({
  selector: 'app-supplier-cancellation-dialog',
  template: `
    <h2 mat-dialog-title>Отмена поставки</h2>
    <mat-dialog-content>
      <div class="context">
        <strong>{{ data.provider }} · заказ поставщику №{{ data.preview.buyId }}</strong>
        <span>{{ data.product }} <ng-container *ngIf="data.article">· {{ data.article }}</ng-container></span>
        <b>Отменить {{ data.preview.quantity | number: '1.0-2' }} шт.</b>
      </div>
      <p>Только неполученный остаток этого товара для клиентского заказа №{{ data.preview.orderId }}.</p>
      <div class="reason-templates" role="group" aria-label="Шаблоны причины отмены">
        <span class="reason-templates-label">Добавить причину из шаблона</span>
        <div class="reason-template-buttons">
          <button mat-stroked-button type="button" *ngFor="let template of reasonTemplates"
            [disabled]="!canAddReasonTemplate(template.text)"
            (click)="addReasonTemplate(template.text); reasonInput.focus()">{{ template.label }}</button>
        </div>
      </div>
      <mat-form-field appearance="outline">
        <mat-label>Причина отмены</mat-label>
        <textarea #reasonInput matInput cdkFocusInitial [formControl]="reason" rows="3" maxlength="1000"
          [readonly]="saving || uncertain"></textarea>
        <mat-error>Укажите причину отмены</mat-error>
      </mat-form-field>
      <p class="error" role="alert" *ngIf="error">{{ error }}</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-stroked-button type="button" (click)="close()" [disabled]="saving">Назад</button>
      <button mat-flat-button color="warn" type="button" (click)="confirm()" [disabled]="saving || !canConfirm">
        {{ saving ? 'Отменяем…' : uncertain ? 'Проверить отмену' : 'Отменить поставку' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .context { display: flex; flex-direction: column; gap: 6px; padding: 12px; border-radius: 8px; background: #f8fafc; }
    .reason-templates { margin: 16px 0 12px; }
    .reason-templates-label { display: block; margin-bottom: 8px; color: #475569; font-size: 12px; }
    .reason-template-buttons { display: flex; flex-wrap: wrap; gap: 6px; }
    .reason-template-buttons button { padding: 0 12px; border-radius: 16px; font-size: 12px; line-height: 30px; }
    mat-form-field { width: 100%; }
    p { color: #475569; line-height: 1.5; }
    .error { color: #b91c1c; }
    mat-dialog-actions { gap: 8px; }
  `]
})
export class SupplierCancellationDialogComponent {
  readonly reason = new UntypedFormControl('', [Validators.required, Validators.maxLength(1000)]);
  readonly reasonTemplates = [
    { label: 'Отказ клиента', text: 'Клиент отказался от товара.' },
    { label: 'Нет у поставщика', text: 'Товара нет в наличии у поставщика.' },
    { label: 'Неверный товар', text: 'Заказан неверный товар.' },
    { label: 'Другой поставщик', text: 'Товар заказан у другого поставщика.' },
    { label: 'Меньше количество', text: 'Клиент уменьшил количество товара.' }
  ];
  saving = false;
  uncertain = false;
  error = '';
  private operationId: string;
  private sentReason = '';

  constructor(
    private cancellation: OrderSupplierCancellationService,
    private dialogRef: MatDialogRef<SupplierCancellationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SupplierCancellationDialogData
  ) {
    this.operationId = cancellation.operationId();
  }

  get canConfirm() {
    return this.reason.valid && Boolean(String(this.reason.value || '').trim());
  }

  canAddReasonTemplate(text: string) {
    const current = String(this.reason.value || '').trim();
    return !this.saving && !this.uncertain &&
      !current.split(/\r?\n/).some((line) => line.trim() === text) &&
      current.length + (current ? 1 : 0) + text.length <= 1000;
  }

  addReasonTemplate(text: string) {
    if (!this.canAddReasonTemplate(text)) return;
    const current = String(this.reason.value || '').trim();
    this.reason.setValue(current ? `${current}\n${text}` : text);
    this.reason.markAsDirty();
  }

  close() {
    if (!this.saving) this.dialogRef.close();
  }

  async confirm() {
    if (this.saving || !this.canConfirm) return;
    const reason = String(this.reason.value).trim();
    if (this.sentReason && this.sentReason !== reason) this.operationId = this.cancellation.operationId();
    this.sentReason = reason;
    this.saving = true;
    this.error = '';
    this.dialogRef.disableClose = true;
    try {
      await this.cancellation.cancel(this.data.preview, reason, this.operationId).toPromise();
      this.dialogRef.close(true);
    } catch (error) {
      const status = Number((error as any)?.status || 0);
      this.uncertain = status === 0 || status >= 500;
      this.error = this.uncertain
        ? 'Не удалось получить подтверждение сервера. Нажмите «Проверить отмену» для безопасного повтора.'
        : extractApiErrorMessage((error as any)?.error || error, 'Не удалось отменить поставку');
    } finally {
      this.saving = false;
      this.dialogRef.disableClose = false;
    }
  }
}
